import { FC } from 'react';
import { ArrowUpRight, Users2, Clock, DollarSign } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useUnifiedAdminMetrics } from '@/hooks/useUnifiedAdminMetrics';

export const UserRetention: FC = () => {
  // Usar el hook de métricas unificadas
  const { data: metrics, isLoading } = useUnifiedAdminMetrics();

  if (isLoading) {
    return <Skeleton className="h-[200px] w-full" />;
  }

  if (!metrics) return null;

  // Calcular métricas de retención basadas en los datos unificados
  const churnRate = metrics.main_stats.churn_rate || 0;
  const retentionRate = 100 - churnRate;
  const avgSubscriptionLength = metrics.subscription_metrics.average_subscription_length || 0;
  
  // Calcular LTV basado en las métricas unificadas
  const ltv = metrics.subscription_metrics.average_ltv || 0;

  return (
    <div className="space-y-6">
      {/* Retención */}
      <div className="bg-green-50 p-6 rounded-lg border border-green-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white rounded-full shadow-sm text-green-600">
              <ArrowUpRight className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold">Retención de Usuarios</h3>
          </div>
          <div className="text-2xl font-bold text-green-700">
            {retentionRate.toFixed(1)}%
          </div>
        </div>
        
        <p className="text-sm text-gray-600 mb-3">Tasa de retención mensual</p>
        
        <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
          <div 
            className="bg-green-600 h-3 rounded-full" 
            style={{ width: `${retentionRate}%` }}
          ></div>
        </div>
        
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Objetivo: 95%</span>
          <span>Actual: {retentionRate.toFixed(1)}%</span>
        </div>
      </div>

      {/* Duración y LTV */}
      <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white rounded-full shadow-sm text-blue-600">
              <Clock className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold">Duración Promedio</h3>
          </div>
          <div className="text-2xl font-bold text-blue-700">
            {avgSubscriptionLength.toFixed(1)} meses
          </div>
        </div>
        
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-medium text-gray-700">LTV Promedio</span>
            </div>
            <span className="text-lg font-semibold text-amber-700">${Math.round(ltv)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
            <div 
              className="bg-amber-500 h-3 rounded-full" 
              style={{ width: `${Math.min(100, (ltv / 500) * 100)}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>$0</span>
            <span>Objetivo: $500</span>
          </div>
        </div>
      </div>
    </div>
  );
};
